import {
    ReapproAlgorithmsFormValues,
    updateReapproCategoryAlgorithms,
    useReapproAlgorithmMatrixForm,
    useReapproCategoryAlgorithms,
    useStores,
} from '@gozoki/api';
import { ReapproAlgorithm } from '@gozoki/api-types';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CSSProperties, memo, useEffect, useMemo, useState } from 'react';
import { Control, useController } from 'react-hook-form';
import { useToast } from '../../components/communication/Toast';

type ReapproCategoryRow = {
    id: number;
    name: string;
};

type ReapproAlgorithmMatrixModalProps = {
    open: boolean;
    onClose: () => void;
    isLoading: boolean;
    reapproCategories: ReapproCategoryRow[];
};

type AlgorithmCellProps = {
    name: `algorithms.${string}`;
    control: Control<ReapproAlgorithmsFormValues>;
};

const defaultReapproAlgorithm: ReapproAlgorithm = 'VMAX';
const reapproAlgorithmOptions: Array<{ value: ReapproAlgorithm; label: string }> = [
    { value: 'VMAX', label: 'VMAX' },
    { value: 'VMJ', label: 'VMJ' },
    { value: 'STOCK_PROJETE', label: 'Stock Projeté' },
];

const AlgorithmCell = memo(({ name, control }: AlgorithmCellProps) => {
    const { field } = useController({
        name,
        control,
        defaultValue: defaultReapproAlgorithm,
    });

    return (
        <Select size="small" value={field.value} onChange={field.onChange} fullWidth>
            {reapproAlgorithmOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </Select>
    );
});

AlgorithmCell.displayName = 'AlgorithmCell';

const ReapproAlgorithmMatrixModal = ({
    open,
    onClose,
    isLoading,
    reapproCategories,
}: ReapproAlgorithmMatrixModalProps) => {
    const [Toast, showToast] = useToast();
    const { data: allStores = [], isLoading: isStoresLoading } = useStores();
    const stores = allStores.filter((store) => store.storeType === 'BOUTIQUE');
    const { data: algorithms = [], isLoading: isAlgorithmsLoading } =
        useReapproCategoryAlgorithms();

    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty },
        getValues,
    } = useReapproAlgorithmMatrixForm();

    const queryClient = useQueryClient();
    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: 'storeLabel', sort: 'asc' },
    ]);

    const updateAlgorithmsMut = useMutation({
        mutationFn: updateReapproCategoryAlgorithms,
        onSuccess: () => {
            showToast({
                message: 'Algorithmes de réappro mis à jour',
                severity: 'success',
            });
            queryClient.invalidateQueries({
                queryKey: ['reapproCategoryAlgorithms'],
            });
            reset(getValues(), { keepDirty: false });
        },
        onError: () => {
            showToast({
                message: "Impossible d'enregistrer les algorithmes de réappro",
                severity: 'error',
            });
        },
    });

    const getAlgorithmKey = (categoryId: number, storeId: number) => `${categoryId}-${storeId}`;

    const isDialogLoading = isLoading || isStoresLoading || isAlgorithmsLoading;
    const hasMatrixData = reapproCategories.length > 0 && stores.length > 0;

    useEffect(() => {
        if (!open) {
            reset({ algorithms: {} }, { keepDirty: false });
            return;
        }
        if (isDialogLoading) {
            return;
        }
        const nextState: Record<string, ReapproAlgorithm> = {};
        algorithms.forEach((entry) => {
            nextState[getAlgorithmKey(entry.reapproCategoryId, entry.storeId)] = entry.algorithm;
        });
        reset({ algorithms: nextState }, { keepDirty: false });
    }, [algorithms, isDialogLoading, open, reset]);

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: 'storeLabel',
                headerName: 'Magasin',
                width: 180,
                sortable: true,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => <span style={storeCellStyle}>{String(params.value)}</span>,
            },
            ...reapproCategories.map((category) => ({
                field: `category_${category.id}`,
                headerName: category.name,
                width: 130,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params: { row: { storeId: number } }) => (
                    <AlgorithmCell
                        name={`algorithms.${getAlgorithmKey(category.id, params.row.storeId)}`}
                        control={control}
                    />
                ),
            })),
        ],
        [control, reapproCategories]
    );

    const rows = useMemo(
        () =>
            stores.map((store) => ({
                id: store.id,
                storeId: store.id,
                storeLabel: store.label,
            })),
        [stores]
    );

    const handleSave = handleSubmit(() => {
        const payload = stores.flatMap((store) =>
            reapproCategories.map((category) => {
                const key = getAlgorithmKey(category.id, store.id);
                const value =
                    (getValues(`algorithms.${key}`) as ReapproAlgorithm | undefined) ??
                    defaultReapproAlgorithm;
                return {
                    storeId: store.id,
                    reapproCategoryId: category.id,
                    algorithm: value,
                };
            })
        );
        updateAlgorithmsMut.mutate(payload);
    });

    if (isDialogLoading) {
        return (
            <Dialog open={open} onClose={onClose}>
                <DialogContent sx={{ paddingTop: '8px' }}>
                    <Typography>Chargement des données...</Typography>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
            <Toast />
            <DialogTitle>Configuration des algorithmes par catégorie et magasin</DialogTitle>
            <DialogContent sx={{ paddingTop: '8px' }}>
                {!hasMatrixData ? (
                    <Typography>
                        Aucune catégorie réappro ou aucun magasin disponible pour la configuration.
                    </Typography>
                ) : (
                    <div style={gridContainerStyle}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            rowHeight={64}
                            columnHeaderHeight={44}
                            sortModel={sortModel}
                            onSortModelChange={setSortModel}
                            sortingOrder={['asc', 'desc']}
                            disableRowSelectionOnClick
                            hideFooter
                            checkboxSelection={false}
                            disableColumnFilter
                            disableColumnSelector
                            disableDensitySelector
                        />
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    Fermer
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!isDirty || updateAlgorithmsMut.isPending}
                >
                    Enregistrer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReapproAlgorithmMatrixModal;

const gridContainerStyle: CSSProperties = {
    height: 520,
    width: '100%',
};

const storeCellStyle: CSSProperties = {
    fontWeight: 600,
    width: '100%',
    textWrap: 'wrap',
};
